import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import type { CompositionV2Entity } from '@revelio/data-access';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { mapCompositionV2EntitiesToV3 } from '../../../filters/utils/composition-v3/role-skill-ent-kind';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PositionedItem,
} from '../../PageConfigProvider/types/item';
import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import {
  convertCompositionToSalaryGrowth,
  convertCompositionToWagePressure,
} from '../market-conditions/market-condition-metrics-utils';
import type { DataProviderChildren, DataProviderSet } from '../types';
import {
  convertCompositionData,
  convertCompositionTopNHeadcountData,
} from '../utils/convert-composition-data';
import { CompositionRawDataProvider } from './CompositionRawDataProvider';
import { CompositionTopCompetitorsDataProvider } from './CompositionTopCompetitorsDataProvider';
import { CompositionTopNDataProvider } from './CompositionTopNDataProvider';
import { CompositionV3RawDataProvider } from './composition-v3/CompositionV3RawDataProvider';
import { CompositionV3TopNDataProvider } from './composition-v3/CompositionV3TopNDataProvider';
import {
  convertCompositionDataV3,
  convertCompositionTopNHeadcountDataV3,
} from './composition-v3/convert-composition-data-v3';
import {
  convertCompositionToSalaryGrowthV3,
  convertCompositionToWagePressureV3,
} from './composition-v3/market-condition-metrics-v3';

type CompositionDataProviderBaseProps = {
  primaryDimension: PrimaryFilterId;
  segment?: SecondaryDimensionValues;
  metric:
    | CompositionMetricValues
    | 'wage_pressure'
    | 'wage_pressure_percentile'
    | 'salary_growth';
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV2Entity[];
  getSelectionBreakdowns?: boolean;
  returnAllSkills?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
};

type TopCompanyHeadcountQuery = {
  type: 'TOP_N';
  segment: 'company' | 'geography';
};
type TopHeadcountQuery = {
  type: 'TOP_HEADCOUNT';
  segment: Exclude<SecondaryDimensionValues, 'company' | 'timeseries'>;
};
type StandardQuery = {
  type: 'STANDARD';
};

type CompositionQuery =
  | TopCompanyHeadcountQuery
  | TopHeadcountQuery
  | StandardQuery;

const getCompositionQueryType = ({
  item,
  dimensionSourceOverride,
}: {
  item: PositionedItem;
  dimensionSourceOverride?: DimensionSource;
}): CompositionQuery => {
  if (item.plot?.chartType !== 'bar') {
    return { type: 'STANDARD' };
  }

  const metric = item.plot?.defaultMetricOptions?.[0];

  if (dimensionSourceOverride && dimensionSourceOverride !== 'primary') {
    return resolveQueryFromDimensionSource(dimensionSourceOverride, metric);
  }

  const dimensionSource = item.plot?.dimensionSource;
  if (dimensionSource && dimensionSource !== 'primary') {
    return resolveQueryFromDimensionSource(dimensionSource, metric);
  }

  return { type: 'STANDARD' };
};

function resolveQueryFromDimensionSource(
  dimensionSource: DimensionSource,
  metric: string | undefined
): CompositionQuery {
  if (dimensionSource === 'primary') return { type: 'STANDARD' };
  if (dimensionSource === 'competitors') {
    return { type: 'TOP_HEADCOUNT', segment: 'competitors' };
  }
  if (
    dimensionSource === 'company' ||
    (dimensionSource === 'geography' && metric !== 'headcount')
  ) {
    return { type: 'TOP_N', segment: dimensionSource };
  }
  if (dimensionSource !== 'timeseries' && metric === 'headcount') {
    return { type: 'TOP_HEADCOUNT', segment: dimensionSource };
  }
  return { type: 'STANDARD' };
}

export const CompositionDataProvider = ({
  primaryDimension,
  segment,
  metric,
  isTimeseries,
  entitiesOverride,
  getSelectionBreakdowns,
  returnAllSkills,
  exchangeRate,
  children,
}: CompositionDataProviderBaseProps) => {
  const { item } = useChart();
  const isSingleEntity = isSingleEntityChart(item);
  const { pageState } = usePageState();
  const queryType = getCompositionQueryType({
    item,
    dimensionSourceOverride: pageState.dimensionSourceOverrides?.[item.id],
  });
  const { revelioPageId } = useTabContentConfig();
  const compositionV3 = useUnleashFlag(FeatureFlag.CompositionV3);

  const entitiesV3 = mapCompositionV2EntitiesToV3(entitiesOverride);

  if (compositionV3) {
    if (metric === 'salary_growth') {
      return (
        <CompositionV3RawDataProvider
          primaryDimension={primaryDimension}
          entitiesOverride={entitiesV3}
          isTimeseries={true}
        >
          {({ data, status, error, monthRefs = [] }) =>
            children({
              data: data?.composition_v3
                ? convertCompositionToSalaryGrowthV3({
                    data: data.composition_v3,
                    chartType: item.plot?.chartType ?? 'line',
                    monthRefs,
                  })
                : undefined,
              status,
              error,
            })
          }
        </CompositionV3RawDataProvider>
      );
    }

    if (metric === 'wage_pressure' || metric === 'wage_pressure_percentile') {
      return (
        <CompositionV3RawDataProvider
          primaryDimension={primaryDimension}
          entitiesOverride={entitiesV3}
          isTimeseries={true}
        >
          {({ data, status, error, monthRefs = [] }) =>
            children({
              data: data?.composition_v3
                ? convertCompositionToWagePressureV3({
                    data: data.composition_v3,
                    monthRefs,
                    variant:
                      metric === 'wage_pressure_percentile'
                        ? 'percentile'
                        : 'value',
                  })
                : undefined,
              status,
              error,
            })
          }
        </CompositionV3RawDataProvider>
      );
    }

    if (queryType.type === 'TOP_N' && !returnAllSkills) {
      return (
        <CompositionV3TopNDataProvider
          primaryDimension={primaryDimension}
          secondaryDimension={queryType.segment}
          metric={metric}
          isTimeseries={isTimeseries}
          exchangeRate={exchangeRate}
        >
          {children}
        </CompositionV3TopNDataProvider>
      );
    }

    if (queryType.type === 'TOP_HEADCOUNT' && !returnAllSkills) {
      const headcountSegment = queryType.segment;
      if (headcountSegment === 'competitors') {
        return (
          <CompositionTopCompetitorsDataProvider
            primaryDimension={primaryDimension}
            metric={metric}
            exchangeRate={exchangeRate}
          >
            {children}
          </CompositionTopCompetitorsDataProvider>
        );
      }

      return (
        <CompositionV3RawDataProvider
          primaryDimension={primaryDimension}
          getSelectionBreakdowns={!revelioPageId}
        >
          {({ data, status, error }) =>
            children({
              data: data?.composition_v3
                ? convertCompositionTopNHeadcountDataV3({
                    segment: headcountSegment,
                    isGrouped:
                      !isSingleEntity && item.plot?.chartType !== 'bar',
                    data: data.composition_v3,
                  })
                : undefined,
              status,
              error,
            })
          }
        </CompositionV3RawDataProvider>
      );
    }

    return (
      <CompositionV3RawDataProvider
        primaryDimension={primaryDimension}
        entitiesOverride={entitiesV3}
        getSelectionBreakdowns={getSelectionBreakdowns}
        returnAllSkills={returnAllSkills}
        isTimeseries={isTimeseries}
      >
        {({ data, status, error, monthRefs = [] }) =>
          children({
            data: data?.composition_v3
              ? convertCompositionDataV3({
                  metric,
                  segment:
                    isTimeseries && !item?.plot?.secondaryDimension
                      ? 'timeseries'
                      : item?.plot?.secondaryDimension || segment,
                  data: data.composition_v3,
                  isTimeseries,
                  exchangeRate,
                  monthRefs,
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionV3RawDataProvider>
    );
  }

  if (metric === 'salary_growth') {
    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        entitiesOverride={entitiesOverride}
        isTimeseries={true}
      >
        {({ data, status, error }) =>
          children({
            data: data?.composition_v2
              ? convertCompositionToSalaryGrowth({
                  data: data.composition_v2,
                  chartType: item.plot?.chartType ?? 'line',
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  if (metric === 'wage_pressure' || metric === 'wage_pressure_percentile') {
    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        entitiesOverride={entitiesOverride}
        isTimeseries={true}
      >
        {({ data, status, error }) =>
          children({
            data: data?.composition_v2
              ? convertCompositionToWagePressure({
                  data: data.composition_v2,
                  variant:
                    metric === 'wage_pressure_percentile'
                      ? 'percentile'
                      : 'value',
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  if (queryType.type === 'TOP_N' && !returnAllSkills) {
    return (
      <CompositionTopNDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={queryType.segment}
        metric={metric}
        isTimeseries={isTimeseries}
        exchangeRate={exchangeRate}
      >
        {children}
      </CompositionTopNDataProvider>
    );
  }

  if (queryType.type === 'TOP_HEADCOUNT' && !returnAllSkills) {
    const headcountSegment = queryType.segment;
    if (headcountSegment === 'competitors') {
      return (
        <CompositionTopCompetitorsDataProvider
          primaryDimension={primaryDimension}
          metric={metric}
          exchangeRate={exchangeRate}
        >
          {children}
        </CompositionTopCompetitorsDataProvider>
      );
    }

    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        getSelectionBreakdowns={!revelioPageId}
      >
        {({ data, status, error }) =>
          children({
            data: data?.composition_v2
              ? convertCompositionTopNHeadcountData({
                  segment: headcountSegment,
                  isGrouped: !isSingleEntity && item.plot?.chartType !== 'bar',
                  data: data?.composition_v2,
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      entitiesOverride={entitiesOverride}
      getSelectionBreakdowns={getSelectionBreakdowns}
      returnAllSkills={returnAllSkills}
      isTimeseries={isTimeseries}
    >
      {({ data, status, error }) =>
        children({
          data: data?.composition_v2
            ? convertCompositionData({
                metric,
                segment:
                  isTimeseries && !item?.plot?.secondaryDimension
                    ? 'timeseries'
                    : item?.plot?.secondaryDimension || segment,
                data: data.composition_v2,
                isTimeseries,
                exchangeRate,
              })
            : undefined,
          status,
          error,
        })
      }
    </CompositionRawDataProvider>
  );
};
